import { User } from '@mi-front/core';
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { getAppUserDisplayName, getUserInitials } from '../../helpers/user';
import { fetchUser } from '../../modules/admin/api';
import { UnknownUserLabel } from './UnknownUserLabel';
import { UserAvatar, UserAvatarSize } from './UserAvatar';

type UserLabelUser = Partial<Pick<User, 'name' | 'surname' | 'email' | 'avatar'>>;

interface UserDisplayProps {
  id?: string;
  user?: UserLabelUser;
  showIcon?: boolean;
  /** Avatar size when showIcon is true. Defaults to small (16px). */
  iconSize?: UserAvatarSize;
}

/** Initials for avatar fallback; tolerates partial user payloads. */
function resolveInitials(user: UserLabelUser) {
  if (user.email) {
    return getUserInitials({
      name: user.name ?? '',
      surname: user.surname ?? '',
      email: user.email,
    });
  }
  if (user.name) {
    return user.name.slice(0, 2).toUpperCase();
  }
  return '?';
}

/** Wraps content with optional avatar (photo or initials) when showIcon is true. */
function wrapWithIcon(
  content: React.ReactNode,
  showIcon: boolean | undefined,
  user: UserLabelUser,
  iconSize: UserAvatarSize,
) {
  if (showIcon) {
    return (
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}
      >
        <UserAvatar size={iconSize} src={user.avatar} initials={resolveInitials(user)} />
        {content}
      </Box>
    );
  }
  return <>{content}</>;
}

/**
 * Renders a user display name from a user object or by fetching the user by id.
 * When the user cannot be found, shows an unknown-user fallback instead of an error alert.
 * With showIcon, shows the user avatar when available, otherwise initials like the account popup.
 */
export function UserLabel({ id, user, showIcon, iconSize = 'small' }: UserDisplayProps) {
  const userQuery = useQuery({
    queryKey: ['user', id],
    queryFn: () => fetchUser(id!),
    staleTime: Infinity,
    enabled: !!id,
  });

  // If user object is provided, use it directly
  if (user) {
    return wrapWithIcon(getAppUserDisplayName(user as User, 'long'), showIcon, user, iconSize);
  }

  // If only ID is provided, fetch user data
  if (id) {
    return (
      <QueryView inline query={userQuery} ignore404>
        {(data) =>
          data ? (
            wrapWithIcon(getAppUserDisplayName(data, 'long'), showIcon, data, iconSize)
          ) : (
            <UnknownUserLabel />
          )
        }
      </QueryView>
    );
  }

  return null;
}
